import folderIcon from '@/assets/icons/folder-icon.svg'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { cn } from '@/lib/utils'
import { useQueryDocuments } from '@/services/folders/queries'
import { DocumentInterface } from '@/services/folders/types'
import { ChevronRight, Home } from 'lucide-react'
import { useCallback, useMemo, useState } from 'react'
import { FinderItem, toFinderItems } from '../types'
import { truncateText } from '../utils'

interface FinderMoveToDialogProps {
    open: boolean
    onClose: () => void
    itemsToMove: FinderItem[]
    onMove: (targetFolderId: string | null) => void
}

interface Breadcrumb {
    id: string | null
    name: string
}

export function FinderMoveToDialog({ open, onClose, itemsToMove, onMove }: FinderMoveToDialogProps) {
    const [currentFolderId, setCurrentFolderId] = useState<string | null>(null)
    const [breadcrumbs, setBreadcrumbs] = useState<Breadcrumb[]>([{ id: null, name: 'Documents' }])

    const itemIdsToExclude = useMemo(() => new Set(itemsToMove.map(i => i.id)), [itemsToMove])

    const { data: rootData, isLoading: isLoadingRoot } = useQueryDocuments({
        enabled: open && !currentFolderId,
        queryParams: {
            data_type: 'info',
            isFolder: 'true',
            limit: '100',
        },
    })

    // Fetch folders inside the currently selected folder (paginated, folders only).
    // Replaces the previous `folder.items` read which was removed from the
    // single-document endpoint to avoid 16MB BSON overflows.
    const { data: childFoldersData, isLoading: isLoadingChildFolders } = useQueryDocuments({
        enabled: open && !!currentFolderId,
        queryParams: {
            parent: currentFolderId as string,
            isFolder: 'true',
            limit: '100',
        },
    })

    const folders = useMemo(() => {
        const rawItems: DocumentInterface[] = currentFolderId
            ? childFoldersData?.data || []
            : rootData?.data || []

        return toFinderItems(rawItems).filter(item => item.type === 'folder' && !itemIdsToExclude.has(item.id))
    }, [currentFolderId, childFoldersData, rootData, itemIdsToExclude])

    const isLoading = isLoadingRoot || isLoadingChildFolders

    const navigateToFolder = useCallback(
        (folderId: string | null, folderName?: string) => {
            setCurrentFolderId(folderId)

            if (folderId === null) {
                setBreadcrumbs([{ id: null, name: 'Documents' }])
            } else {
                const existingIndex = breadcrumbs.findIndex(b => b.id === folderId)
                if (existingIndex >= 0) {
                    setBreadcrumbs(breadcrumbs.slice(0, existingIndex + 1))
                } else {
                    setBreadcrumbs([...breadcrumbs, { id: folderId, name: folderName || 'Folder' }])
                }
            }
        },
        [breadcrumbs]
    )

    const handleMove = useCallback(() => {
        onMove(currentFolderId)
        onClose()
        setCurrentFolderId(null)
        setBreadcrumbs([{ id: null, name: 'Documents' }])
    }, [onMove, onClose, currentFolderId])

    const handleClose = useCallback(() => {
        onClose()
        setCurrentFolderId(null)
        setBreadcrumbs([{ id: null, name: 'Documents' }])
    }, [onClose])

    const itemNames = itemsToMove.map(i => i.name).join(', ')

    return (
        <Dialog open={open} onOpenChange={isOpen => !isOpen && handleClose()}>
            <DialogContent className='max-w-md p-0 overflow-hidden bg-white'>
                <DialogHeader className='px-5 py-4 border-b border-gray-100'>
                    <DialogTitle className='text-base font-medium'>Move to...</DialogTitle>
                    <p className='text-xs text-gray-400 mt-1'>Moving: {truncateText(itemNames, 50)}</p>
                </DialogHeader>

                <div className='px-5 py-2 border-b border-gray-50 bg-gray-50/50'>
                    <div className='flex items-center gap-1 text-sm'>
                        {breadcrumbs.map((crumb, index) => (
                            <div key={crumb.id ?? 'root'} className='flex items-center'>
                                {index > 0 && <ChevronRight className='w-3 h-3 mx-1 text-gray-300' />}
                                <button
                                    onClick={() => navigateToFolder(crumb.id, crumb.name)}
                                    className={cn(
                                        'flex items-center gap-1 px-1 py-0.5 rounded transition-colors',
                                        index === breadcrumbs.length - 1 ? 'text-gray-700' : 'text-gray-400 hover:text-gray-700'
                                    )}
                                >
                                    {index === 0 && <Home className='w-3 h-3' />}
                                    {crumb.name}
                                </button>
                            </div>
                        ))}
                    </div>
                </div>

                <div className='max-h-72 overflow-y-auto p-2'>
                    {isLoading ? (
                        <div className='flex items-center justify-center py-8'>
                            <div className='w-6 h-6 border-2 border-gray-300 border-t-[#63316B] rounded-full animate-spin' />
                        </div>
                    ) : folders.length === 0 ? (
                        <p className='text-center text-sm text-gray-400 py-8'>No folders</p>
                    ) : (
                        folders.map(folder => (
                            <button
                                key={folder.id}
                                onClick={() => navigateToFolder(folder.id, folder.name)}
                                className='w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors cursor-pointer hover:bg-gray-100'
                            >
                                <img src={folderIcon} alt='' className='w-8 h-8' />
                                <span className='flex-1 text-left text-sm text-gray-700'>{folder.name}</span>
                                <ChevronRight className='w-4 h-4 text-gray-400' />
                            </button>
                        ))
                    )}
                </div>

                <div className='flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-100 bg-gray-50/50'>
                    <Button variant='outline' onClick={handleClose}>
                        Cancel
                    </Button>
                    <Button onClick={handleMove} className='bg-[#63316B] hover:bg-[#4a2550]'>
                        Move Here
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    )
}
